import { useEffect, lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { Login } from '@/components/Login';
import { MailLayout } from '@/features/mailbox/MailLayout';
import { MailboxPage } from '@/features/mailbox/MailboxPage';
import { RailLayout } from '@/components/RailLayout';
import { useAuth } from '@/contexts/useAuth';
import { AccountModalsHost } from '@/features/modals/AccountModalsHost';
import { RuleModalHost } from '@/features/modals/RuleModalHost';
import { SignatureModalHost } from '@/features/modals/SignatureModalHost';
import { TodoModalHost } from '@/features/todo/TodoModalHost';
import { CalendarModalHost } from '@/features/calendar/CalendarModalHost';

// P17 (PERF_AUDIT): defer the heavy, non-critical surfaces out of the initial
// mailbox bundle. The compose editor pulls in the whole TipTap/ProseMirror stack
// (the `editor` vendor chunk) and only mounts when composing; the settings host
// and the non-default workspace pages likewise load on demand. MailboxPage/Login
// stay eager — they ARE the first paint.
const ComposeModalHost = lazy(() =>
  import('@/features/modals/ComposeModalHost').then((m) => ({ default: m.ComposeModalHost }))
);
const SettingsModalHost = lazy(() =>
  import('@/features/modals/SettingsModalHost').then((m) => ({ default: m.SettingsModalHost }))
);
const TodoPage = lazy(() => import('@/features/todo/TodoPage').then((m) => ({ default: m.TodoPage })));
const CalendarPage = lazy(() =>
  import('@/features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const NotesPage = lazy(() => import('@/features/notes/NotesPage').then((m) => ({ default: m.NotesPage })));
const TriagePage = lazy(() =>
  import('@/features/inbox-triage/TriagePage').then((m) => ({ default: m.TriagePage }))
);
const SurveysPage = lazy(() =>
  import('@/features/surveys/SurveysPage').then((m) => ({ default: m.SurveysPage }))
)
const SubscriptionsPage = lazy(() =>
  import('@/features/subscriptions/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage }))
);
import { CalendarEventDetailPopover } from '@/features/calendar/components/CalendarEventDetailPopover';
import { CalendarQuickCreate } from '@/features/calendar/components/CalendarQuickCreate';
import { CommandPalette } from '@/components/CommandPalette';
import { FolderPickerDialog } from '@/components/FolderPickerDialog';
import { SenderProfileSheet } from '@/components/SenderProfileSheet';
import { useUIStore } from '@/stores/uiStore';
import { parseUrlRoute, getHashRoute } from '@/lib/urlRouting';

export function AppShell() {
  const { isAuthenticated, login } = useAuth();
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);
  const setActiveWorkspace = useUIStore((state) => state.setActiveWorkspace);
  const mailView = useUIStore((state) => state.mailView);
  const setMailView = useUIStore((state) => state.setMailView);

  // Parse initial URL on mount and handle browser navigation
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleUrlChange = () => {
      const { pathname, search } = getHashRoute();
      const route = parseUrlRoute(pathname, search);
      setActiveWorkspace(route.workspace);
      if (route.workspace === 'mail') {
        // Reader deep links ('#/account/…/email/…') parse as 'mailbox'; a
        // pushed reader URL never lands here (pushes don't fire popstate), so
        // Back from a reader opened inside briefing still restores the
        // briefing route before this runs.
        setMailView(route.mailView ?? 'mailbox');
      }
    };

    // Set workspace from initial URL
    handleUrlChange();

    // Handle browser back/forward
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [isAuthenticated, setActiveWorkspace, setMailView]);

  // Warm every code-split workspace page shortly after boot so switching apps
  // or mail tabs never suspends (the visible symptom was the whole window
  // blanking for a beat on first visit to each view). Same import specifiers
  // as the lazy() calls above → same chunks, so this is pure prefetch.
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = window.setTimeout(() => {
      void import('@/features/todo/TodoPage');
      void import('@/features/calendar/CalendarPage');
      void import('@/features/notes/NotesPage');
      void import('@/features/inbox-triage/TriagePage');
      void import('@/features/surveys/SurveysPage');
      void import('@/features/subscriptions/SubscriptionsPage');
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  const mailWorkspaceView =
    mailView === 'triage' ? (
      <TriagePage />
    ) : mailView === 'surveys' ? (
      <SurveysPage />
    ) : mailView === 'subscriptions' ? (
      <SubscriptionsPage />
    ) : (
      <MailboxPage />
    );

  const workspaceView = activeWorkspace === 'todo'
    ? <TodoPage />
    : activeWorkspace === 'calendar'
      ? <CalendarPage />
      : activeWorkspace === 'notes'
        ? <NotesPage />
        : mailWorkspaceView;

  return (
    <>
      <MailLayout>
        {/* Non-default workspace pages are code-split and prefetched after
            boot; if a switch still races the prefetch, the fallback keeps the
            full chrome (brand cap, header, rail, dock) so only the content
            pane shows a spinner instead of the whole window blanking. */}
        <Suspense
          fallback={
            <RailLayout sidebar={<div className="hidden h-full surface-dark md:block" />}>
              <div className="flex flex-1 items-center justify-center glass-page">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            </RailLayout>
          }
        >
          {workspaceView}
        </Suspense>
        {/* Modal hosts render null while closed, so the fallback is never
            visible. Note: lazy() starts the import on first RENDER of the host
            (i.e. at app mount), not on first open — the chunks download in the
            background off the synchronous critical path, which is the actual
            win; opening compose in the first instants may briefly wait for the
            in-flight chunk. */}
        <Suspense fallback={null}>
          <ComposeModalHost />
          <SettingsModalHost />
        </Suspense>
        <AccountModalsHost />
        <SignatureModalHost />
        <RuleModalHost />
        <TodoModalHost />
        <CalendarModalHost />
        <CalendarEventDetailPopover />
        <CalendarQuickCreate />
        <CommandPalette />
        <FolderPickerDialog />
        <SenderProfileSheet />
      </MailLayout>
    </>
  );
}
